'use client';

import * as React from 'react';
import { Pencil } from 'lucide-react';
import { clsx } from 'clsx';
import { Avatar } from './Avatar';
import { Tag } from './Tag';
import styles from './CandidateProfileCard.module.css';

export interface CandidateProfileCardProps {
  avatarSrc?: string;
  avatarAlt?: string;
  /** Blurs the photo — used for anonymized candidates. @see Avatar */
  blur?: boolean;
  name: string;
  /**
   * Name to hash for the avatar's initials-tile colour when it should differ
   * from `name` — an anonymised candidate's `name` is rewritten to
   * "Candidate N", which would otherwise recolour their tile away from the one
   * the candidate selector shows beside it. @see Avatar
   */
  colorName?: string;
  /** Optional status chip rendered above the name (e.g. "Anonymized") */
  statusChip?: React.ReactNode;
  positionTitle?: string;
  /** Pipe-separated highlights line, e.g. "Location | Salary | Availability" */
  highlights?: string;
  description?: string;
  /** Called when the "Change avatar" pill is clicked. Omit to hide the pill. */
  onUpdateAvatar?: () => void;
  /** Called when "Edit details" is clicked. Omit to hide the link. */
  onEditDetails?: () => void;
  /** Rendered below the photo — e.g. an anonymize control */
  actions?: React.ReactNode;
  /** Rendered below the header content, full width, inside the card — e.g. a Tabs component */
  tabs?: React.ReactNode;
  className?: string;
}

export const CandidateProfileCard = React.forwardRef<HTMLDivElement, CandidateProfileCardProps>(
  (
    {
      avatarSrc,
      avatarAlt,
      blur = false,
      name,
      colorName,
      statusChip,
      positionTitle,
      highlights,
      description,
      onUpdateAvatar,
      onEditDetails,
      actions,
      tabs,
      className,
    },
    ref
  ) => {
    return (
      <div ref={ref} className={clsx(styles.card, className)}>
        <div className={styles.body}>
          <div className={styles.photoColumn}>
            <div className={styles.photoWrapper}>
              <Avatar
                src={avatarSrc}
                alt={avatarAlt || name}
                name={name}
                colorName={colorName}
                size="xxl"
                variant="rounded"
                blur={blur}
                className={styles.photo}
              />
              {onUpdateAvatar && (
                <button
                  type="button"
                  className={styles.updateAvatarButton}
                  onClick={onUpdateAvatar}
                >
                  <Tag variant="default" color="info" size="sm">
                    Change avatar
                  </Tag>
                </button>
              )}
            </div>
            {actions}
          </div>

          <div className={styles.content}>
            {onEditDetails && (
              <button type="button" className={styles.editButton} onClick={onEditDetails}>
                Edit details
                <Pencil size={16} className={styles.editIcon} />
              </button>
            )}
            {statusChip && <div className={styles.statusChip}>{statusChip}</div>}
            <h2 className={styles.name}>{name}</h2>
            {positionTitle && <h4 className={styles.positionTitle}>{positionTitle}</h4>}
            {highlights && <p className={styles.highlights}>{highlights}</p>}
            {description && <p className={styles.description}>{description}</p>}
          </div>
        </div>

        {tabs && <div className={styles.tabs}>{tabs}</div>}
      </div>
    );
  }
);

CandidateProfileCard.displayName = 'CandidateProfileCard';

export default CandidateProfileCard;
